<template>
    <v-dialog v-model="dialog" max-width="690" :fullscreen="$vuetify.breakpoint.xs">
        <v-card class="py-8 px-11">
            <ValidationObserver ref="form" v-slot="{ handleSubmit }">
                <v-form @submit.prevent="handleSubmit(save)">
                    <h2 class="text-center">{{$t('global.edit')}} {{$t('blog.post')}}{{$t('global.y')}}</h2>

                    <v-card-text class="px-0">
                        <ValidationProvider :name="$t('blog.title')" rules="required|max:80" v-slot="{ errors }">
                            <v-text-field :label="$t('blog.title')"
                                          v-model="post.title"
                                          :loading="loadingRequest"
                            >
                                <template v-slot:prepend-inner>
                                    <svg v-svg="'icon-title'" size="15" class="mr-1"></svg>
                                </template>
                            </v-text-field>
                            <small class="font-weight-bold error--text">{{ errors[0] }}</small>
                        </ValidationProvider>
                    </v-card-text>

                    <v-card-text class="px-0">
                        <label for="file" class="text-decoration-underline green--text font-weight-bold">{{$t('blog.choose')}}</label>
                        <ValidationProvider vid="picture" :name="$t('blog.choose')" rules="mimes:jpeg,png,gif,webp" v-slot="{ validate, errors }">
                            <input type="file" id="file" accept="image/jpeg,image/png,image/gif,image/webp" ref="picture" @change="previewFiles() || validate($event)"><br>
                            <small class="font-weight-bold error--text">{{ errors[0] }}</small>
                        </ValidationProvider>
                        <v-img :src="imagePreview" width="150px" class="mt-3" v-if="imagePreview"></v-img>
                        <v-img :src="`${API_URL}/storage/${post.picture}`" width="200px" class="mt-3" v-else-if="dialog && !loadingRequest"></v-img>
                    </v-card-text>

                    <v-card-text class="px-0">
                        <ValidationProvider :name="$t('blog.description')" rules="required" v-slot="{ errors }">
                            <ckeditor :editor="editor"
                                      v-model="post.description"
                                      :config="editorConfig"
                                      class="my-7"
                            ></ckeditor>
                            <small class="font-weight-bold error--text">{{ errors[0] }}</small>
                        </ValidationProvider>
                    </v-card-text>

                    <v-card-text class="px-0">
                        <ValidationProvider vid="external_link" :name="$t('blog.external_link')" :rules="{ regex: /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/ }" v-slot="{ errors }">
                            <v-text-field :label="$t('blog.external_link')"
                                          v-model="post.external_link"
                                          :loading="loadingRequest"
                            >
                                <template v-slot:prepend-inner>
                                    <svg v-svg="'icon-link'" size="15" class="mr-1"></svg>
                                </template>
                            </v-text-field>
                            <small class="font-weight-bold error--text">{{ errors[0] }}</small>
                        </ValidationProvider>
                    </v-card-text>

                    <v-card-text class="px-0">
                        <ValidationProvider vid="video_link" :name="$t('blog.video_link')" :rules="{ regex: /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/ }" v-slot="{ errors }">
                            <v-text-field :label="$t('blog.video_link')"
                                          v-model="post.video_link"
                                          :loading="loadingRequest"
                            >
                                <template v-slot:prepend-inner>
                                    <svg v-svg="'icon-link'" size="15" class="mr-1"></svg>
                                </template>
                            </v-text-field>
                            <small class="font-weight-bold error--text">{{ errors[0] }}</small>
                        </ValidationProvider>
                    </v-card-text>

                    <v-card-actions class="d-flex justify-center mt-6">
                        <v-btn class="green white--text mr-4" type="submit" :loading="loadingSave">{{$t('global.save')}}</v-btn>
                        <v-btn class="transparent green--text ml-4" @click="dialog = false">{{$t('global.close')}}</v-btn>
                    </v-card-actions>
                </v-form>
            </ValidationObserver>
        </v-card>
    </v-dialog>
</template>


<script>
    import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
    export default {
        props: ['post_id'],
        data() {
            return {
                dialog: false,
                post: {},
                loadingRequest: false,
                loadingSave: false,
                picture: '',
                imagePreview: '',
                editor: ClassicEditor,
                editorConfig: {},
            }
        },

        watch: {
            'dialog': function (newVal) {
                if(newVal) {
                    this.loadingRequest = true;
                    this.imagePreview = '';
                    this.picture = '';
                    this.$api('posts').show(this.post_id).then((res) => {
                        this.post = res.data.post;
                    }).finally(() => {
                        this.loadingRequest = false;
                    })
                }
            }
        },

        methods: {
            save() {
                this.loadingSave = true;

                let params = new FormData();
                params.append('picture', this.picture);
                params.append('title', this.post.title);
                params.append('description', this.post.description);
                params.append('external_link', this.post.external_link);
                params.append('video_link', this.post.video_link);

                this.$api('posts').update(params, this.post_id)
                    .then(() => {
                        this.$parent.$emit('getPosts');
                        this.$store.dispatch('alert', {type: 1, message: this.$t('global.success_edited')});
                        this.dialog = false;
                    }).catch(err => {
                        this.$refs.form.setErrors(err.response.data);
                    }).finally(() => {
                        this.loadingSave = false;
                    })
            },

            previewFiles() {
                this.picture = this.$refs.picture.files[0];

                let reader = new FileReader();
                reader.addEventListener('load', function () {
                    this.imagePreview = reader.result;
                }.bind(this), false);

                if (this.picture) {
                    if (/\.(jpe?g|png|gif|webp)$/i.test(this.picture.name)) {
                        reader.readAsDataURL(this.picture);
                    }
                }
            }
        },
    }
</script>

<style scoped lang="scss">
    @import "../../scss/components/dialog";
</style>